import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useToast } from '../hooks/use-toast';
import { ChevronRight, ChevronLeft, CheckCircle, XCircle } from 'lucide-react';

const WarmupMultiStepConsultationForm = ({ title = "Get in touch with us", buttonText = "Get free consultation" }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showMessage, setShowMessage] = useState(false);
  const [formData, setFormData] = useState({
    ageConfirmation: '',
    fraudConfirmation: '',
    amountLost: '',
    caseOutline: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [detectedCountry, setDetectedCountry] = useState('us');
  const [userIP, setUserIP] = useState('');
  const [urlParams, setUrlParams] = useState({
    affiliate_encoded_id: '',
    transaction_id: '',
    tid: '',
    sid: '',
    aid: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Extract URL parameters on component mount
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const affiliateId = urlSearchParams.get('affiliate_encoded_id') || '';
    const transactionId = urlSearchParams.get('transaction_id') || '';
    const tid = urlSearchParams.get('tid') || '';
    const sid = urlSearchParams.get('sid') || '';
    const aid = urlSearchParams.get('aid') || '';
    
    setUrlParams({
      affiliate_encoded_id: affiliateId,
      transaction_id: transactionId,
      tid: tid,
      sid: sid,
      aid: aid
    });
  }, []);

  // Auto-detect country and IP based on user's location
  useEffect(() => {
    const detectCountryAndIP = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data.country_code) {
          setDetectedCountry(data.country_code.toLowerCase());
        }
        if (data.ip) {
          setUserIP(data.ip);
        }
      } catch (error) {
        console.log('Could not detect country and IP, using defaults');
        setDetectedCountry('us');
        setUserIP('');
      }
    };
    
    detectCountryAndIP();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Show message immediately after selection for steps 1, 2, and 3
    if ((field === 'ageConfirmation' && value !== '') || 
        (field === 'fraudConfirmation' && value !== '') ||
        (field === 'amountLost' && value !== '')) {
      setShowMessage(true);
    }
  };

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      setShowMessage(false);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setShowMessage(false);
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return formData.ageConfirmation !== '' && showMessage;
      case 2:
        return formData.fraudConfirmation !== '' && showMessage;
      case 3:
        return formData.amountLost !== '' && showMessage;
      case 4:
        return formData.caseOutline.trim() !== '';
      case 5:
        return formData.firstName.trim() !== '' && 
               formData.lastName.trim() !== '' && 
               formData.email.trim() !== '' && 
               formData.phone.trim() !== '';
      default:
        return false;
    }
  };

  const getWarmupMessage = () => {
    if (currentStep === 1 && formData.ageConfirmation && showMessage) {
      if (formData.ageConfirmation === 'no') {
        return {
          type: 'error',
          text: 'Unfortunately, we can only work with adults aged 18 and above. Thank you for your honesty. Stay safe online!'
        };
      } else {
        return {
          type: 'success',
          text: 'Perfect. You\'re eligible to proceed. Let\'s assess your case together.'
        };
      }
    }

    if (currentStep === 2 && formData.fraudConfirmation && showMessage) {
      if (formData.fraudConfirmation === 'no') {
        return {
          type: 'error',
          text: 'Based on your answer, you may not qualify for our service. We\'re focused on high-value recovery cases over $2,000. If you believe there\'s been a mistake, contact our team.'
        };
      } else {
        return {
          type: 'success',
          text: 'You are not alone. This month alone, we\'ve helped over 3,000 people start their recovery journey. Our team specializes in complex scams, and we\'re here to fight for you.'
        };
      }
    }

    if (currentStep === 3 && formData.amountLost && showMessage) {
      const messages = {
        '$2,000–$5,000': 'We\'ve helped clients recover cases in this exact range. Every dollar matters. Let\'s build your case.',
        '$5,000–$10,000': 'Every case counts. We\'ve successfully recovered thousands in this exact range for other clients.',
        '$10,000–$20,000': 'This is a substantial amount. Our legal and recovery experts will treat your case with priority.',
        '$20,000–$50,000': 'This case size requires specialized handling. Our team is trained for exactly this type of recovery.',
        '$50,000 or more': 'High-value case detected. Your situation requires immediate strategic attention. Let\'s prepare together.'
      };
      return {
        type: 'success',
        text: messages[formData.amountLost] || 'Let\'s continue building your case.'
      };
    }

    return null;
  };

  const isDisqualified = () => {
    return (formData.ageConfirmation === 'no') || (formData.fraudConfirmation === 'no');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare payload with IP address and URL parameters
      const payload = {
        // Combine first and last name for compatibility with existing webhook
        fullName: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        country: detectedCountry.toUpperCase(),
        amountLost: formData.amountLost,
        caseOutline: formData.caseOutline,
        // Multi-step specific fields
        ageConfirmation: formData.ageConfirmation,
        fraudConfirmation: formData.fraudConfirmation,
        firstName: formData.firstName,
        lastName: formData.lastName,
        // System fields
        ipAddress: userIP,
        submissionTime: new Date().toISOString(),
        formType: 'warmup-multi-step',
        // Include URL parameters when available
        ...(urlParams.affiliate_encoded_id && { affiliate_encoded_id: urlParams.affiliate_encoded_id }),
        ...(urlParams.transaction_id && { transaction_id: urlParams.transaction_id }),
        ...(urlParams.tid && { tid: urlParams.tid }),
        ...(urlParams.sid && { sid: urlParams.sid }),
        ...(urlParams.aid && { aid: urlParams.aid })
      };

      // Submit to webhook
      const response = await fetch('https://hook.eu2.make.com/2vgr2hrnl6grsv83b4vofpdf4pblgjjp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast({
          title: "Consultation Requested",
          description: "Thank you! We'll contact you within 24 hours for your free evaluation.",
          variant: "default"
        });

        // Reset form
        setFormData({
          ageConfirmation: '',
          fraudConfirmation: '',
          amountLost: '',
          caseOutline: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: ''
        });
        setCurrentStep(1);
        setShowMessage(false);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      toast({
        title: "Submission Error",
        description: "There was an error submitting your form. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-black mb-6 font-clash">Are you 18 years or older?</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer p-4 border-2 rounded-lg transition-all duration-200 hover:bg-gray-50" 
                     style={{ borderColor: formData.ageConfirmation === 'yes' ? '#FF3D00' : '#E5E5E5' }}>
                <input
                  type="radio"
                  name="ageConfirmation"
                  value="yes"
                  checked={formData.ageConfirmation === 'yes'}
                  onChange={(e) => handleInputChange('ageConfirmation', e.target.value)}
                  className="w-4 h-4 text-orange-600"
                />
                <span className="text-base font-medium">Yes</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer p-4 border-2 rounded-lg transition-all duration-200 hover:bg-gray-50"
                     style={{ borderColor: formData.ageConfirmation === 'no' ? '#FF3D00' : '#E5E5E5' }}>
                <input
                  type="radio"
                  name="ageConfirmation"
                  value="no"
                  checked={formData.ageConfirmation === 'no'}
                  onChange={(e) => handleInputChange('ageConfirmation', e.target.value)}
                  className="w-4 h-4 text-orange-600"
                />
                <span className="text-base font-medium">No</span>
              </label>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-black mb-6 font-clash">Have you been defrauded of $2,000 or more through an online scam?</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer p-4 border-2 rounded-lg transition-all duration-200 hover:bg-gray-50"
                     style={{ borderColor: formData.fraudConfirmation === 'yes' ? '#FF3D00' : '#E5E5E5' }}>
                <input
                  type="radio"
                  name="fraudConfirmation"
                  value="yes"
                  checked={formData.fraudConfirmation === 'yes'}
                  onChange={(e) => handleInputChange('fraudConfirmation', e.target.value)}
                  className="w-4 h-4 text-orange-600"
                />
                <span className="text-base font-medium">Yes</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer p-4 border-2 rounded-lg transition-all duration-200 hover:bg-gray-50"
                     style={{ borderColor: formData.fraudConfirmation === 'no' ? '#FF3D00' : '#E5E5E5' }}>
                <input
                  type="radio"
                  name="fraudConfirmation"
                  value="no"
                  checked={formData.fraudConfirmation === 'no'}
                  onChange={(e) => handleInputChange('fraudConfirmation', e.target.value)}
                  className="w-4 h-4 text-orange-600"
                />
                <span className="text-base font-medium">No</span>
              </label>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-black mb-6 font-clash">How much money did you lose?</h3>
            <div className="space-y-3">
              {['$2,000–$5,000', '$5,000–$10,000', '$10,000–$20,000', '$20,000–$50,000', '$50,000 or more'].map((amount) => (
                <label key={amount} className="flex items-center space-x-3 cursor-pointer p-4 border-2 rounded-lg transition-all duration-200 hover:bg-gray-50"
                       style={{ borderColor: formData.amountLost === amount ? '#FF3D00' : '#E5E5E5' }}>
                  <input
                    type="radio"
                    name="amountLost"
                    value={amount}
                    checked={formData.amountLost === amount}
                    onChange={(e) => handleInputChange('amountLost', e.target.value)}
                    className="w-4 h-4 text-orange-600"
                  />
                  <span className="text-base font-medium">{amount}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-black mb-6 font-clash">Please outline your case:</h3>
            <textarea
              value={formData.caseOutline}
              onChange={(e) => handleInputChange('caseOutline', e.target.value)}
              className="w-full bg-[#F4F4F4] border-0 rounded-lg px-4 py-3 min-h-[120px] resize-none outline-none text-base"
              placeholder="Please describe what happened, who was involved, and any other relevant details..."
              rows={5}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-black mb-6 font-clash">Contact Information</h3>
            
            {/* First Name and Last Name side by side */}
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full bg-[#F4F4F4] border-0 rounded-lg px-4 py-3 outline-none text-base"
                  placeholder="First Name"
                />
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full bg-[#F4F4F4] border-0 rounded-lg px-4 py-3 outline-none text-base"
                  placeholder="Last Name"
                />
              </div>
            </div>

            {/* Email */}
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full bg-[#F4F4F4] border-0 rounded-lg px-4 py-3 outline-none text-base"
              placeholder="Email"
            />

            {/* Phone Input with Country Detection */}
            <div className="phone-input-container">
              <PhoneInput
                country={detectedCountry}
                value={formData.phone}
                onChange={(phone) => handleInputChange('phone', phone)}
                inputProps={{
                  placeholder: 'Phone number'
                }}
                enableSearch={true}
                disableSearchIcon={true}
                countryCodeEditable={false}
                specialLabel=""
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const warmupMessage = getWarmupMessage();

  return (
    <div className="bg-white p-8 rounded-[20px] shadow-lg max-w-md w-full">
      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Step {currentStep} of 5</span>
          <span className="text-sm text-gray-600">{Math.round((currentStep / 5) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${(currentStep / 5) * 100}%`,
              backgroundColor: '#FF3D00'
            }}
          ></div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {renderStep()}

        {/* Warmup Message */}
        {warmupMessage && (
          <div 
            className={`p-4 rounded-lg border-l-4 ${
              warmupMessage.type === 'error' 
                ? 'bg-red-50 border-red-500 text-red-700' 
                : 'bg-green-50 border-green-500 text-green-700'
            }`}
          >
            <div className="flex items-start">
              {warmupMessage.type === 'error' ? (
                <XCircle className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" />
              ) : (
                <CheckCircle className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" />
              )}
              <p className="text-sm font-medium leading-relaxed">
                {warmupMessage.text}
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 pt-4">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="flex items-center justify-center px-4 py-3 text-gray-600 border border-gray-300 rounded-lg transition-all duration-200 hover:bg-gray-50"
            >
              <ChevronLeft size={20} className="mr-1" />
              Back
            </button>
          )}
          
          {currentStep < 5 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={!canProceedToNextStep() || isDisqualified()}
              className="flex-1 flex items-center justify-center text-white font-bold py-3 rounded-lg transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: (canProceedToNextStep() && !isDisqualified()) ? '#FF3D00' : '#CCCCCC',
                border: 'none',
                cursor: (canProceedToNextStep() && !isDisqualified()) ? 'pointer' : 'not-allowed'
              }}
              onMouseEnter={(e) => {
                if (canProceedToNextStep() && !isDisqualified()) {
                  e.target.style.backgroundColor = '#E53500';
                }
              }}
              onMouseLeave={(e) => {
                if (canProceedToNextStep() && !isDisqualified()) {
                  e.target.style.backgroundColor = '#FF3D00';
                }
              }}
            >
              {isDisqualified() ? 'Thank you' : 'Next'}
              {!isDisqualified() && <ChevronRight size={20} className="ml-1" />}
            </button>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting || !canProceedToNextStep()}
              className="flex-1 text-white font-bold py-3 rounded-lg transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: (canProceedToNextStep() && !isSubmitting) ? '#FF3D00' : '#CCCCCC',
                border: 'none',
                cursor: (canProceedToNextStep() && !isSubmitting) ? 'pointer' : 'not-allowed'
              }}
              onMouseEnter={(e) => {
                if (canProceedToNextStep() && !isSubmitting) {
                  e.target.style.backgroundColor = '#E53500';
                }
              }}
              onMouseLeave={(e) => {
                if (canProceedToNextStep() && !isSubmitting) {
                  e.target.style.backgroundColor = '#FF3D00';
                }
              }}
            >
              {isSubmitting ? 'Submitting...' : buttonText}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default WarmupMultiStepConsultationForm;